import { useState, useEffect } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  SUBSCRIPTION_PLANS,
  SubscriptionRole,
} from "../../utils/subscriptionPlans";
import { toast } from "sonner";

const STORAGE_KEY = "reelcat_has_seen_pricing_popup";

export function FirstTimePricingPopup() {
  const user = useQuery(api.users.getCurrentUser);
  const createCheckout = useAction(api.payments.createCheckout);
  const [isOpen, setIsOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is authenticated and hasn't seen the popup
    if (user && !localStorage.getItem(STORAGE_KEY)) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, "true");
  };

  const handleUpgrade = async (planType: "starter" | "pro") => {
    setIsUpgrading(planType);
    try {
      const result = await createCheckout({
        planType,
        returnUrl: window.location.origin + window.location.pathname,
      });

      if (result.checkout_url) {
        // Mark as seen before redirecting
        localStorage.setItem(STORAGE_KEY, "true");
        window.location.href = result.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to initiate checkout");
      console.error(error);
      setIsUpgrading(null);
    }
  };

  const handleContinueFree = () => {
    handleClose();
    toast.success("Welcome to reelcat! You can upgrade anytime from your account settings.");
  };

  if (!isOpen) return null;

  const starterPlan = SUBSCRIPTION_PLANS.find((p) => p.role === "starter");
  const proPlan = SUBSCRIPTION_PLANS.find((p) => p.role === "pro");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with gradient background */}
        <div className="relative px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10 text-white rounded-t-xl overflow-hidden flex-shrink-0">
          {/* Background gradient that extends fully */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-400 to-orange-600 rounded-t-xl"></div>
          <div className="absolute inset-0 bg-black/10 rounded-t-xl"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <span className="text-xl sm:text-2xl font-bold text-white">R</span>
                </div>
                <span className="text-xl sm:text-2xl font-bold">reelcat</span>
              </div>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
              Welcome to reelcat! 🎉
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl">
              Create viral shorts in seconds with AI-powered video editing.
              Choose a plan that fits your creative goals.
            </p>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
            {/* Starter Plan */}
            {starterPlan && (
              <div className="border-2 border-blue-200 rounded-xl p-4 sm:p-5 md:p-6 hover:border-blue-400 transition-all hover:shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {starterPlan.name}
                  </h3>
                  <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap flex-shrink-0">
                    POPULAR
                  </span>
                </div>
                <div className="mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                    ${starterPlan.price}
                  </span>
                  <span className="text-gray-600 text-sm sm:text-base">/month</span>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  {starterPlan.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-start text-sm text-gray-700"
                    >
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUpgrade("starter")}
                  disabled={!!isUpgrading}
                  className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {isUpgrading === "starter" ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                      <span className="text-xs sm:text-sm">Processing...</span>
                    </>
                  ) : (
                    <>
                      Get Started
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Pro Plan */}
            {proPlan && (
              <div className="border-2 border-purple-200 rounded-xl p-4 sm:p-5 md:p-6 hover:border-purple-400 transition-all hover:shadow-lg bg-gradient-to-br from-purple-50 to-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100/50 rounded-full blur-3xl"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {proPlan.name}
                    </h3>
                    <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200 whitespace-nowrap flex-shrink-0">
                      BEST VALUE
                    </span>
                  </div>
                  <div className="mb-4 sm:mb-6">
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                      ${proPlan.price}
                    </span>
                    <span className="text-gray-600 text-sm sm:text-base">/month</span>
                  </div>
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {proPlan.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-start text-sm text-gray-700"
                      >
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleUpgrade("pro")}
                    disabled={!!isUpgrading}
                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {isUpgrading === "pro" ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                        <span className="text-xs sm:text-sm">Processing...</span>
                      </>
                    ) : (
                      <>
                        Go Pro
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Continue with Free Plan */}
          <div className="text-center pt-3 sm:pt-4 border-t border-gray-200">
            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
              Not ready to commit? Start with the free plan
            </p>
            <button
              onClick={handleContinueFree}
              className="text-gray-700 hover:text-gray-900 font-medium text-xs sm:text-sm underline underline-offset-2 hover:underline-offset-4 transition-all"
            >
              Continue with Free Plan
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex-shrink-0">
          <p className="text-xs text-gray-500 text-center">
            ✨ All plans include a 30-day money-back guarantee. Cancel anytime, no questions asked.
          </p>
        </div>
      </div>
    </div>
  );
}
